import {
	FlashTimerExtras,
	PqTimerExtras,
	TimerExtras,
	TimeSpan
} from "../../lib/timer";
import { Annotations } from "../../lib/userdata";
import { AttemptId, ItemId, ProductId } from "./index";
import { ListItem } from "./list";

export interface AnswerValue<A extends AttemptId, I extends ItemId> {
	id: string;
	attempt_id: A;
	item_id: I;
	name: string;
	answer: string;
	correct: boolean;
}

export type QuestionType =
	'multiplechoice'
	| 'multiplecorrect'
	| 'numeric'
	| 'cloze'
	| 'matching'
	| 'sorting'
	| 'constructedresponse'
	| 'unknown';

export type FlashcardType =
	'flashcard'
	| 'colorflashcard';

export type ItemType = QuestionType | FlashcardType;

export interface ContentItem<I extends ItemId> {
	id: I,
	name: string,
	type: ItemType,
	section_title: string,
	contexts: {
		id: string,
		content_html: string,
		style: string
	}[],
	multiplechoice?: {
		answers: {
			content: string,
			correct: boolean,
			name: string
		}[],
		explanation: string,
		prompt: string,
		style: string
	},
	multiplecorrect?: {
		answers: {
			content: string,
			correct: boolean,
			name: string
		}[],
		explanation: string,
		prompt: string,
		style: string
	},
	numeric?: {
		answers: {
			maximum?: {
				text: string,
				value: number
			},
			minimum?: {
				text: string,
				value: number
			},
			number: {
				text: string,
				value: number
			}
		},
		explanation: string,
		prompt: string,
		style: string
	},
	cloze?: {
		answers: {
			name: string,
			correct: string,
			accept?: {
				text: string,
				regex: boolean
			}[]
		}[],
		explanation: string,
		prompt: string,
		style: string
	},
	matching?: {
		answers: {
			term: string,
			definition: string,
			term_order: number,
			definition_order: number,
			explanation: string
		}[],
		explanation: string,
		prompt: string,
		style: string
	},
	sorting?: {
		answers: {
			content: string,
			display_order: number,
			correct_order: number,
			explanation: string
		}[],
		explanation: string,
		prompt: string,
		style: string
	},
	constructedresponse?: {
		explanation: string,
		prompt: string,
		style: string
	},
	flashcard?: {
		question: string,
		answer: string,
		style: string,
	},
}

export interface ItemUsage<I extends ItemId, E> {
	item: ListItem<I>;
	time: TimeSpan<E>;
}

export type PqItemUsage<I extends ItemId> = ItemUsage<I, PqTimerExtras>
export type FlashItemUsage<I extends ItemId> = ItemUsage<I, FlashTimerExtras>

export interface ItemUsageResponse<A extends AttemptId, I extends ItemId, E extends TimerExtras> {
	times: {
		id: string;
		content_user_id: string;
		attempt_id: A;
		item_id: I;
		start_time: string;
		end_time: string;
		review: boolean;
		extras?: E;
	}[];
}

export type PqItemUsageResponse<A extends AttemptId, I extends ItemId> = ItemUsageResponse<A, I, PqTimerExtras>;
export type FlashItemUsageResponse<A extends AttemptId, I extends ItemId> = ItemUsageResponse<A, I, FlashTimerExtras>;

export type UsageMode = "pq_test" | "pq_review" | "flash";

export abstract class ItemDataSource<P extends ProductId, A extends AttemptId, I extends ItemId, E extends TimerExtras> {
	/**
	 * @param {string} product
	 * @param {number} item
	 * @param {boolean} randomize
	 * @returns {Promise<ContentItem>}
	 */
	abstract getItem(
		product: P,
		item: I,
		randomize: boolean
	): Promise<ContentItem<I>>

	/**
	 * @param a {ContentItem}
	 * @param b {ContentItem}
	 * @returns {ContentItem["contexts]}
	 */
	getSameContexts(a: ContentItem<I>, b: ContentItem<I>): ContentItem<I>["contexts"] {
		return a.contexts.filter(first =>
			b.contexts.some(second => first.id === second.id)
		);
	}

	/**
	 * @param product {string}
	 * @param attempt {number}
	 * @param item {string}
	 * @param data {string}
	 * @returns {Promise<{
	 *     id:string,
	 *     attempt_id:number,
	 *     set_id:string,
	 *     item_id:string,
	 *     name:string,
	 *     answer:string,
	 *     correct:boolean
	 * }>}
	 */
	abstract setUserData(
		product: P,
		attempt: A,
		item: I,
		data: Annotations
	): Promise<AnswerValue<A, I>>

	/**
	 * @param product {string}
	 * @param attempt {number}
	 * @param review {boolean}
	 * @param mode {string}
	 * @param times {{
	 *     item:ContentItem,
	 *     time:{
	 *         start:string,
	 *         end:string,
	 *         contexts_viewed:boolean
	 *     }
	 * }[]}
	 * @returns {Promise<ItemUsageResponse>}
	 */
	abstract setPing(
		product: P,
		attempt: A,
		mode: UsageMode,
		times: ItemUsage<I, E>[],
	): Promise<void>

	/**
	 * @param product {string}
	 * @param attempt {number}
	 * @returns {Promise<string>}
	 */
	abstract setFinished(product: P, attempt: A): Promise<string>
}

export abstract class PqItemDataSource<P extends ProductId, A extends AttemptId, I extends ItemId> extends ItemDataSource<P, A, I, PqTimerExtras> {
	abstract setPing(
		product: P,
		attempt: A,
		mode: UsageMode,
		times: PqItemUsage<I>[]
	): Promise<void>
}

export abstract class FlashItemDataSource<P extends ProductId, A extends AttemptId, I extends ItemId> extends ItemDataSource<P, A, I, FlashTimerExtras> {
	abstract setPing(
		product: P,
		attempt: A,
		mode: UsageMode,
		times: FlashItemUsage<I>[]
	): Promise<void>
}

export class NullItemDataSource<P extends ProductId, A extends AttemptId, I extends ItemId> extends ItemDataSource<P, A, I, never> {
	getItem(product: P, item: I, randomize: boolean): Promise<ContentItem<I>> {
		throw new Error("Method not implemented.");
	}

	setUserData(product: P, attempt: A, item: I, data: Annotations): Promise<AnswerValue<A, I>> {
		throw new Error("Method not implemented.");
	}

	setPing(product: P, attempt: A, mode: UsageMode, times: ItemUsage<I, never>[]): Promise<void> {
		throw new Error("Method not implemented.");
	}

	setFinished(product: P, attempt: A): Promise<string> {
		throw new Error("Method not implemented.");
	}
}
